import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useId,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { Checkbox } from 'antd';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import {
  getTeamMemberDisplayName,
  getTeamMemberKey,
} from '../../services/dsrTechnicianTeam';

const TechnicianTeamMemberMultiSelect = ({
  members = [],
  selectedKeys = [],
  onChange,
  disabled = false,
  placeholder = 'Search by name or email…',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [panelStyle, setPanelStyle] = useState({ top: 0, left: 0, width: 320 });
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const genId = useId();
  const listboxId = `tech-member-ms-${String(genId).replace(/\W/g, '')}`;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (members || [])
      .map((member) => {
        const key = getTeamMemberKey(member);
        const name = getTeamMemberDisplayName(member);
        const email = member.email || '';
        const roleName = member.roleName || member.role?.name || '—';
        return { key, member, name, email, roleName };
      })
      .filter((row) => {
        if (!q) return true;
        return (
          row.name.toLowerCase().includes(q)
          || row.email.toLowerCase().includes(q)
          || row.roleName.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [members, search]);

  const selectedCount = selectedKeys.length;

  const updatePanelPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPanelStyle({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(280, r.width),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePanelPosition();
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePanelPosition]);

  const isInside = useCallback((node) => {
    if (!node) return false;
    if (containerRef.current?.contains(node)) return true;
    if (panelRef.current?.contains(node)) return true;
    return false;
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!open) return;
      if (isInside(e.target)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, isInside]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const toggleKey = (key) => {
    onChange(
      selectedKeys.includes(key)
        ? selectedKeys.filter((k) => k !== key)
        : [...selectedKeys, key],
    );
  };

  const triggerLabel = selectedCount > 0
    ? `${selectedCount} team member${selectedCount === 1 ? '' : 's'} selected`
    : placeholder;

  const panel = open && !disabled
    ? (
      <div
        ref={panelRef}
        className="modern-dsr-tech-dd__panel modern-dsr-tech-dd__panel--portal modern-dsr-tech-member-ms__panel"
        id={`${listboxId}-listbox`}
        role="listbox"
        aria-multiselectable="true"
        style={{
          position: 'fixed',
          top: panelStyle.top,
          left: panelStyle.left,
          width: panelStyle.width,
          zIndex: 10050,
        }}
      >
        <div className="modern-dsr-tech-dd__search">
          <Search size={14} className="modern-dsr-tech-dd__search-icon" color="#9CA3AF" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Search…"
            className="modern-dsr-tech-dd__search-input"
            autoComplete="off"
            autoFocus
            aria-label="Filter team members"
          />
        </div>
        <div className="modern-dsr-tech-member-ms__list">
          {members.length === 0 && (
            <div className="modern-dsr-tech-dd__empty">All project team members are already on this log</div>
          )}
          {members.length > 0 && rows.length === 0 && (
            <div className="modern-dsr-tech-dd__empty">No matches for your search</div>
          )}
          {rows.map((row) => (
            <div
              key={row.key}
              role="option"
              aria-selected={selectedKeys.includes(row.key)}
              className={`modern-dsr-tech-member-ms__opt${
                selectedKeys.includes(row.key) ? ' modern-dsr-tech-member-ms__opt--active' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (disabled) return;
                toggleKey(row.key);
              }}
            >
              <Checkbox
                checked={selectedKeys.includes(row.key)}
                disabled={disabled}
                onChange={() => toggleKey(row.key)}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="modern-dsr-tech-member-ms__opt-text">
                <span className="modern-dsr-tech-member-ms__name" title={row.name}>
                  {row.name}
                </span>
                <span className="modern-dsr-tech-member-ms__meta" title={`${row.email} · ${row.roleName}`}>
                  {[row.email, row.roleName].filter(Boolean).join(' · ')}
                </span>
              </div>
            </div>
          ))}
        </div>
        {members.length > 0 && (
          <div className="modern-dsr-tech-member-ms__foot">
            {selectedCount} of {members.length} selected
          </div>
        )}
      </div>
    )
    : null;

  return (
    <div
      ref={containerRef}
      className="modern-dsr-tech-dd modern-dsr-tech-member-ms"
      data-open={open ? 'true' : 'false'}
      data-disabled={disabled ? 'true' : 'false'}
    >
      <button
        type="button"
        className="modern-dsr-tech-dd__trigger"
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${listboxId}-listbox`}
      >
        <span
          className={
            selectedCount > 0
              ? 'modern-dsr-tech-dd__value'
              : 'modern-dsr-tech-dd__value modern-dsr-tech-dd__value--placeholder'
          }
          title={triggerLabel}
        >
          {triggerLabel}
        </span>
        {open
          ? <ChevronUp size={16} color="#6B7280" className="modern-dsr-tech-dd__chev" />
          : <ChevronDown size={16} color="#6B7280" className="modern-dsr-tech-dd__chev" />
        }
      </button>
      {typeof document !== 'undefined' && createPortal(panel, document.body)}
    </div>
  );
};

export default TechnicianTeamMemberMultiSelect;
